import { cn } from '../lib/cn';
import { mapPaint } from '../tokens';

export type RoutePoint = readonly [number, number];

export interface SchematicMapProps {
  /** Polyline in the SVG's own coordinate space. */
  route?: readonly RoutePoint[];
  width?: number;
  height?: number;
  className?: string;
}

/**
 * The flat cartographic map the design uses on the hero, the search results and
 * the trip detail: water and park blocks, a street grid, two arterials, and the
 * trip route drawn as a white-cased pōhutukawa line between a ringed origin and
 * a filled destination.
 *
 * This is the seam where MapLibre GL and OpenStreetMap tiles land in a later
 * phase — the component boundary and props stay, the internals change.
 */
export function SchematicMap({
  route = DEFAULT_ROUTE,
  width = 420,
  height = 420,
  className,
}: SchematicMapProps) {
  const points = route.map((point) => point.join(',')).join(' ');
  const origin = route[0] ?? [0, 0];
  const destination = route[route.length - 1] ?? [0, 0];

  const grid: React.ReactNode[] = [];
  for (let x = 0; x <= width; x += 46) {
    grid.push(
      <line
        key={`v${x}`}
        x1={x}
        y1={0}
        x2={x}
        y2={height}
        stroke={mapPaint.grid}
        strokeWidth={1}
      />,
    );
  }
  for (let y = 0; y <= height; y += 46) {
    grid.push(
      <line key={`h${y}`} x1={0} y1={y} x2={width} y2={y} stroke={mapPaint.grid} strokeWidth={1} />,
    );
  }

  return (
    <div
      className={cn('absolute inset-0', className)}
      style={{ background: mapPaint.land }}
      role="img"
      aria-label="Route overview map"
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid slice"
        className="block h-full w-full"
      >
        <rect x={0} y={0} width={width} height={height} fill={mapPaint.land} />
        <rect
          x={width * 0.62}
          y={0}
          width={width * 0.4}
          height={height * 0.5}
          fill={mapPaint.water}
        />
        <rect
          x={0}
          y={height * 0.74}
          width={width * 0.45}
          height={height * 0.4}
          fill={mapPaint.water}
        />
        <rect
          x={width * 0.1}
          y={height * 0.18}
          width={70}
          height={54}
          rx={4}
          fill={mapPaint.park}
        />
        <rect
          x={width * 0.55}
          y={height * 0.58}
          width={90}
          height={70}
          rx={4}
          fill={mapPaint.park}
        />
        {grid}
        <Street x1={0} y1={height * 0.5} x2={width} y2={height * 0.34} width={9} />
        <Street x1={width * 0.3} y1={0} x2={width * 0.42} y2={height} width={9} />
        <Street
          x1={0}
          y1={height * 0.78}
          x2={width}
          y2={height * 0.62}
          width={6}
          colour={mapPaint.roadMinor}
        />
        <Street
          x1={width * 0.66}
          y1={0}
          x2={width * 0.78}
          y2={height}
          width={6}
          colour={mapPaint.roadMinor}
        />
        <polyline
          points={points}
          fill="none"
          stroke={mapPaint.routeCasing}
          strokeWidth={9}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <polyline
          points={points}
          fill="none"
          stroke={mapPaint.routeLine}
          strokeWidth={5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle
          cx={origin[0]}
          cy={origin[1]}
          r={9}
          fill="#fff"
          stroke={mapPaint.originRing}
          strokeWidth={4}
        />
        <circle
          cx={destination[0]}
          cy={destination[1]}
          r={9}
          fill={mapPaint.destinationFill}
          stroke="#fff"
          strokeWidth={3}
        />
      </svg>
    </div>
  );
}

function Street({
  x1,
  y1,
  x2,
  y2,
  width,
  colour = mapPaint.road,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  colour?: string;
}) {
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={colour}
      strokeWidth={width}
      strokeLinecap="round"
    />
  );
}

const DEFAULT_ROUTE: readonly RoutePoint[] = [
  [40, 330],
  [95, 300],
  [150, 250],
  [150, 250],
  [210, 230],
  [260, 180],
  [300, 150],
  [340, 110],
  [380, 70],
];

/** The routes the export draws on each screen, kept together for reuse. */
export const MAP_ROUTES = {
  hero: [
    [40, 360],
    [80, 330],
    [120, 300],
    [150, 250],
    [180, 210],
    [150, 170],
    [190, 130],
    [250, 120],
    [300, 90],
    [350, 60],
  ],
  search: [
    [40, 470],
    [90, 420],
    [150, 380],
    [150, 300],
    [210, 250],
    [260, 200],
    [300, 150],
    [340, 100],
    [380, 60],
  ],
} as const satisfies Record<string, readonly RoutePoint[]>;

export interface MapRouteChipProps {
  from: string;
  to: string;
  className?: string;
}

/** The floating origin → destination chip pinned inside the search map. */
export function MapRouteChip({ from, to, className }: MapRouteChipProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-line bg-surface/95 px-3 py-2',
        'text-meta font-semibold shadow-chip backdrop-blur-sm',
        className,
      )}
    >
      <span className="h-2 w-2 rounded-full bg-ngahere" />
      {from}
      <span className="text-line-arrow">→</span>
      <span className="h-2 w-2 rounded-full bg-pohutukawa" />
      {to}
    </div>
  );
}
